/**
 * GUI Builder - Utility for creating lil-gui style interface elements
 * Uses CSS classes from gui.css for consistent, maintainable styling
 */
export default class GUIBuilder {
  /**
   * Create a GUI panel
   * @param {string} title - Panel title
   * @returns {Object} Panel elements
   */
  static createPanel(title) {
    const panel = document.createElement('div');
    panel.className = 'gui-panel';

    const header = document.createElement('div');
    header.className = 'gui-header';

    const titleEl = document.createElement('h1');
    titleEl.className = 'gui-title';
    titleEl.textContent = title;

    // Collapse button only (no close button)
    const collapseBtn = document.createElement('button');
    collapseBtn.className = 'gui-minimize';
    collapseBtn.innerHTML = '−';
    collapseBtn.title = 'Collapse panel';

    header.appendChild(titleEl);
    header.appendChild(collapseBtn);

    const content = document.createElement('div');
    content.className = 'gui-content';

    panel.appendChild(header);
    panel.appendChild(content);

    // Add collapse functionality
    let isMinimized = false;
    collapseBtn.addEventListener('click', () => {
      isMinimized = !isMinimized;
      panel.classList.toggle('minimized', isMinimized);
      collapseBtn.innerHTML = isMinimized ? '+' : '−';
      collapseBtn.title = isMinimized ? 'Expand panel' : 'Collapse panel';
    });

    return { panel, header, titleEl, collapseBtn, content };
  }

  /**
   * Create a toggle button
   * @param {string} text - Button text
   * @returns {HTMLButtonElement}
   */
  static createToggleButton(text) {
    const btn = document.createElement('button');
    btn.className = 'gui-toggle';
    btn.textContent = text;
    return btn;
  }

  /**
   * Create a collapsible section
   * @param {string} title - Section title
   * @param {boolean} collapsed - Initial collapsed state
   * @returns {Object} Section elements
   */
  static createSection(title, collapsed = false) {
    const section = document.createElement('div');
    section.className = `gui-section${collapsed ? ' collapsed' : ''}`;

    const header = document.createElement('div');
    header.className = 'gui-section-header';

    const titleEl = document.createElement('h3');
    titleEl.className = 'gui-section-title';
    titleEl.textContent = title;

    const chevron = document.createElement('span');
    chevron.className = 'gui-section-chevron';
    chevron.innerHTML = '▼';

    header.appendChild(titleEl);
    header.appendChild(chevron);

    const body = document.createElement('div');
    body.className = 'gui-section-body';

    section.appendChild(header);
    section.appendChild(body);

    // Add click handler to toggle
    header.addEventListener('click', () => {
      section.classList.toggle('collapsed');
    });

    return { section, header, titleEl, chevron, body };
  }

  /**
   * Create a subsection (non-collapsible group within a section)
   * @param {string} title - Subsection title
   * @returns {Object} Subsection elements
   */
  static createSubsection(title) {
    const subsection = document.createElement('div');
    subsection.className = 'gui-subsection';

    if (title) {
      const titleEl = document.createElement('h4');
      titleEl.className = 'gui-subsection-title';
      titleEl.textContent = title;
      subsection.appendChild(titleEl);
    }

    return { subsection };
  }

  /**
   * Create a control row
   * @param {string} label - Label text
   * @param {HTMLElement} control - Control element
   * @param {boolean} stacked - Stack vertically
   * @returns {HTMLElement}
   */
  static createRow(label, control, stacked = false) {
    const row = document.createElement('div');
    row.className = stacked ? 'gui-row gui-row-stacked' : 'gui-row';

    if (label) {
      const labelEl = document.createElement('label');
      labelEl.className = stacked ? 'gui-label gui-label-stacked' : 'gui-label';
      labelEl.textContent = label;
      row.appendChild(labelEl);
    }

    if (control) {
      row.appendChild(control);
    }

    return row;
  }

  /**
   * Create a text input
   * @param {string} value - Initial value
   * @param {string} type - Input type (text, number, etc.)
   * @returns {HTMLInputElement}
   */
  static createInput(value = '', type = 'text') {
    const input = document.createElement('input');
    input.className = 'gui-input';
    input.type = type;
    input.value = value;
    return input;
  }

  /**
   * Create a number input
   * @param {number} value - Initial value
   * @param {number} min - Min value
   * @param {number} max - Max value
   * @param {number} step - Step value
   * @returns {HTMLInputElement}
   */
  static createNumberInput(value, min, max, step) {
    const input = GUIBuilder.createInput(value, 'number');
    if (min !== undefined) input.min = min;
    if (max !== undefined) input.max = max;
    if (step !== undefined) input.step = step;
    return input;
  }

  /**
   * Create a select dropdown
   * @param {Array} options - Array of {value, label} objects
   * @param {string} selected - Selected value
   * @returns {HTMLSelectElement}
   */
  static createSelect(options, selected = '') {
    const select = document.createElement('select');
    select.className = 'gui-select';

    options.forEach(opt => {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.label || opt.value;
      if (opt.value === selected) {
        option.selected = true;
      }
      select.appendChild(option);
    });

    return select;
  }

  /**
   * Create a slider with value display
   * @param {number} min - Min value
   * @param {number} max - Max value
   * @param {number} value - Initial value
   * @param {number} step - Step value
   * @param {Function} onChange - Change callback
   * @returns {Object} Slider elements
   */
  static createSlider(min, max, value, step, onChange) {
    const container = document.createElement('div');
    container.className = 'gui-slider-row';

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'gui-slider';
    slider.min = min;
    slider.max = max;
    slider.value = value;
    slider.step = step;

    const valueInput = GUIBuilder.createNumberInput(value, min, max, step);
    valueInput.className = 'gui-input gui-slider-value';

    slider.addEventListener('input', () => {
      valueInput.value = slider.value;
      if (onChange) onChange(parseFloat(slider.value));
    });

    valueInput.addEventListener('change', () => {
      slider.value = valueInput.value;
      if (onChange) onChange(parseFloat(valueInput.value));
    });

    container.appendChild(slider);
    container.appendChild(valueInput);

    return { container, slider, valueInput };
  }

  /**
   * Create XYZ scale controls with link/unlink
   * @param {Function} onScaleChange - Callback (axis, value)
   * @param {Function} onLinkToggle - Callback (linked)
   * @returns {Object} Control elements
   */
  static createXYZControl(onScaleChange, onLinkToggle) {
    const control = document.createElement('div');
    control.className = 'gui-xyz-control';

    // Header with link button
    const header = document.createElement('div');
    header.className = 'gui-xyz-header';

    const label = document.createElement('label');
    label.className = 'gui-label';
    label.textContent = 'Scale:';

    const linkBtn = document.createElement('button');
    linkBtn.className = 'gui-button gui-button-success gui-link-button';
    linkBtn.textContent = '🔗 Linked';
    linkBtn.dataset.linked = 'true';

    header.appendChild(label);
    header.appendChild(linkBtn);
    control.appendChild(header);

    const controls = {};
    const axes = ['x', 'y', 'z'];
    const colors = { x: '#ef6461', y: '#7ec850', z: '#5c9ded' };

    axes.forEach(axis => {
      const axisRow = document.createElement('div');
      axisRow.className = 'gui-xyz-axis';

      const axisLabel = document.createElement('span');
      axisLabel.className = 'gui-xyz-label';
      axisLabel.textContent = axis.toUpperCase();
      axisLabel.style.color = colors[axis];

      const sliderControl = GUIBuilder.createSlider(0.1, 5, 1, 0.1, (value) => {
        const isLinked = linkBtn.dataset.linked === 'true';
        if (isLinked) {
          axes.forEach(a => {
            controls[a].slider.value = value;
            controls[a].valueInput.value = value;
          });
        }
        if (onScaleChange) onScaleChange(axis, value, isLinked);
      });

      axisRow.appendChild(axisLabel);
      axisRow.appendChild(sliderControl.container);
      control.appendChild(axisRow);

      controls[axis] = sliderControl;
    });

    linkBtn.addEventListener('click', () => {
      const isLinked = linkBtn.dataset.linked === 'true';
      const newLinked = !isLinked;
      linkBtn.dataset.linked = newLinked.toString();

      if (newLinked) {
        linkBtn.textContent = '🔗 Linked';
        linkBtn.className = 'gui-button gui-button-success gui-link-button';
      } else {
        linkBtn.textContent = '⛓️‍💥 Unlinked';
        linkBtn.className = 'gui-button gui-link-button';
        linkBtn.style.background = '#4a3a2d';
        linkBtn.style.borderColor = '#6a4a3d';
      }

      if (onLinkToggle) onLinkToggle(newLinked);
    });

    return { control, controls, linkBtn };
  }

  /**
   * Create a button
   * @param {string} text - Button text
   * @param {string} variant - Button variant (primary, success, danger)
   * @param {boolean} full - Full width
   * @returns {HTMLButtonElement}
   */
  static createButton(text, variant = '', full = false) {
    const btn = document.createElement('button');
    let className = 'gui-button';
    if (variant) className += ` gui-button-${variant}`;
    if (full) className += ' gui-button-full';
    btn.className = className;
    btn.textContent = text;
    return btn;
  }

  /**
   * Create a button group
   * @param {Array} buttons - Array of button elements
   * @returns {HTMLElement}
   */
  static createButtonGroup(buttons) {
    const group = document.createElement('div');
    group.className = 'gui-button-group';
    buttons.forEach(btn => group.appendChild(btn));
    return group;
  }

  /**
   * Create a color picker
   * @param {string} value - Initial color value
   * @returns {HTMLInputElement}
   */
  static createColorPicker(value = '#ffffff') {
    const input = document.createElement('input');
    input.type = 'color';
    input.className = 'gui-color';
    input.value = value;
    return input;
  }

  /**
   * Create a checkbox
   * @param {string} label - Checkbox label
   * @param {boolean} checked - Initial checked state
   * @returns {Object} Checkbox elements
   */
  static createCheckbox(label, checked = false) {
    const container = document.createElement('div');
    container.className = 'gui-checkbox-container';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'gui-checkbox';
    checkbox.checked = checked;

    const labelEl = document.createElement('label');
    labelEl.className = 'gui-checkbox-label';
    labelEl.textContent = label;

    container.appendChild(checkbox);
    container.appendChild(labelEl);

    // Make label clickable
    labelEl.addEventListener('click', () => {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change'));
    });

    return { container, checkbox, labelEl };
  }

  /**
   * Create a badge
   * @param {string} text - Badge text
   * @param {string} variant - Badge variant
   * @returns {HTMLElement}
   */
  static createBadge(text, variant = '') {
    const badge = document.createElement('span');
    badge.className = `gui-badge${variant ? ` gui-badge-${variant}` : ''}`;
    badge.textContent = text;
    return badge;
  }

  /**
   * Create a divider
   * @returns {HTMLElement}
   */
  static createDivider() {
    const divider = document.createElement('hr');
    divider.className = 'gui-divider';
    return divider;
  }

  /**
   * Create info text
   * @param {string} text - Info text
   * @returns {HTMLElement}
   */
  static createInfo(text) {
    const info = document.createElement('div');
    info.className = 'gui-info';
    info.textContent = text;
    return info;
  }

  /**
   * Create a minimizable panel (collapses to thin top bar)
   * @param {string} title - Panel title
   * @param {Function} onMinimize - Callback when minimize state changes (isMinimized)
   * @returns {Object} Panel elements with minimize functionality
   */
  static createMinimizablePanel(title, onMinimize) {
    const panel = document.createElement('div');
    panel.className = 'gui-panel';

    const header = document.createElement('div');
    header.className = 'gui-header';

    const titleEl = document.createElement('h1');
    titleEl.className = 'gui-title';
    titleEl.textContent = title;

    // Collapse button only (no close button)
    const minimizeBtn = document.createElement('button');
    minimizeBtn.className = 'gui-minimize';
    minimizeBtn.innerHTML = '−';
    minimizeBtn.title = 'Collapse panel';

    header.appendChild(titleEl);
    header.appendChild(minimizeBtn);

    const content = document.createElement('div');
    content.className = 'gui-content';

    panel.appendChild(header);
    panel.appendChild(content);

    // State management
    let isMinimized = false;

    minimizeBtn.addEventListener('click', () => {
      isMinimized = !isMinimized;
      panel.classList.toggle('minimized', isMinimized);
      minimizeBtn.innerHTML = isMinimized ? '+' : '−';
      minimizeBtn.title = isMinimized ? 'Expand panel' : 'Collapse panel';
      if (onMinimize) onMinimize(isMinimized);
    });

    return {
      panel,
      header,
      titleEl,
      content,
      minimizeBtn,
      isMinimized: () => isMinimized,
      setMinimized: (minimized) => {
        if (minimized !== isMinimized) {
          minimizeBtn.click();
        }
      }
    };
  }

  /**
   * Create a color-coded collapsible section
   * @param {string} title - Section title
   * @param {string} colorTheme - Color theme (primary, success, warning, danger, info)
   * @param {boolean} collapsed - Initial collapsed state
   * @returns {Object} Section elements
   */
  static createColorCodedSection(title, colorTheme, collapsed = false) {
    const section = this.createSection(title, collapsed);

    // Apply color theme
    section.section.dataset.theme = colorTheme;
    section.section.classList.add('gui-section-themed');

    return section;
  }

  /**
   * Create an animated subsection with indentation
   * @param {string} title - Subsection title
   * @param {number} indentLevel - Indentation level (1-3)
   * @param {boolean} collapsed - Initial collapsed state
   * @returns {Object} Subsection elements
   */
  static createAnimatedSubsection(title, indentLevel = 1, collapsed = false) {
    const subsection = document.createElement('div');
    subsection.className = `gui-subsection gui-subsection-animated gui-indent-${indentLevel}`;
    if (collapsed) subsection.classList.add('collapsed');

    const header = document.createElement('div');
    header.className = 'gui-subsection-header';

    const chevron = document.createElement('span');
    chevron.className = 'gui-subsection-chevron';
    chevron.innerHTML = '▸';

    const titleEl = document.createElement('h4');
    titleEl.className = 'gui-subsection-title';
    titleEl.textContent = title;

    header.appendChild(chevron);
    header.appendChild(titleEl);

    const body = document.createElement('div');
    body.className = 'gui-subsection-body';

    subsection.appendChild(header);
    subsection.appendChild(body);

    // Toggle handler
    header.addEventListener('click', () => {
      subsection.classList.toggle('collapsed');
    });

    return { subsection, header, titleEl, body, chevron };
  }

  /**
   * Create a preset selector with grid layout
   * @param {Array} presets - Array of {value, label, thumbnail} objects
   * @param {Function} onSelect - Callback when preset selected
   * @returns {Object} Preset selector elements
   */
  static createPresetSelector(presets, onSelect) {
    const container = document.createElement('div');
    container.className = 'gui-preset-selector';

    const grid = document.createElement('div');
    grid.className = 'gui-preset-grid';

    presets.forEach(preset => {
      const item = document.createElement('div');
      item.className = 'gui-preset-item';
      item.title = preset.label;

      if (preset.thumbnail) {
        const img = document.createElement('img');
        img.src = preset.thumbnail;
        img.alt = preset.label;
        item.appendChild(img);
      }

      const label = document.createElement('span');
      label.textContent = preset.label;
      item.appendChild(label);

      item.addEventListener('click', () => {
        grid.querySelectorAll('.gui-preset-item').forEach(i =>
          i.classList.remove('active')
        );
        item.classList.add('active');
        if (onSelect) onSelect(preset);
      });

      grid.appendChild(item);
    });

    container.appendChild(grid);

    return { container, grid };
  }

  /**
   * Create a slider group (multiple labeled sliders)
   * @param {Array} sliders - Array of {label, min, max, value, step, onChange} objects
   * @param {string} orientation - 'vertical' or 'horizontal'
   * @returns {HTMLElement} Slider group container
   */
  static createSliderGroup(sliders, orientation = 'vertical') {
    const group = document.createElement('div');
    group.className = `gui-slider-group gui-slider-group-${orientation}`;

    sliders.forEach(config => {
      const slider = this.createSlider(
        config.min,
        config.max,
        config.value,
        config.step,
        config.onChange
      );

      const row = this.createRow(config.label, slider.container);
      group.appendChild(row);
    });

    return group;
  }
}
